import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { badgeUrl } from "../lib/api";

/**
 * Floating self-audit badge. We scan our own domain on a schedule and
 * publish the result — clicking through opens our public report.
 *
 * Hidden on:
 *  - any /report/* page (would be a weird recursion)
 *  - the /verify/:token magic-link landing
 *  - the /dashboard (user wants to focus, not see marketing)
 *  - if the user has dismissed it this session (sessionStorage flag)
 *
 * Freshness: the SVG endpoint sets a 30-second max-age + an ETag bound
 * to the latest scan id, so the badge picks up score improvements on the
 * next page load (or after at most ~60 seconds for users who keep the
 * tab open). For the self-audit widget specifically we also append a
 * coarse 5-minute timestamp slot to the URL so any aggressive CDN
 * intermediate (e.g. Cloudflare browser cache) is bypassed.
 */
const SELF_DOMAIN =
  typeof window !== "undefined"
    ? window.location.hostname.replace(/^www\./, "")
    : "";

const HIDDEN_PREFIXES = ["/report/", "/verify/", "/dashboard", "/auth/"];
const DISMISS_KEY = "pl_self_badge_dismissed_v1";

export default function SelfBadge() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
      } catch {
        /* sessionStorage may be unavailable in some privacy modes — fine */
      }
    }
  }, []);

  if (!SELF_DOMAIN || dismissed || imgFailed) return null;
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  // Coarse 5-minute slot cache-buster: changes every 5 minutes so the
  // self-audit badge picks up score updates within minutes, but multiple
  // simultaneous page views within the same slot still benefit from edge
  // and browser caching.
  const cacheSlot = Math.floor(Date.now() / (5 * 60 * 1000));
  const svg = `${badgeUrl(SELF_DOMAIN, {
    variant: "pill",
    theme: "dark",
    size: "sm",
  })}${
    badgeUrl(SELF_DOMAIN).includes("?") ? "&" : "?"
  }v=${cacheSlot}`;

  return (
    <aside
      className="fixed bottom-4 left-4 z-30 group"
      data-testid="self-badge-widget"
      aria-label="PrivacyLens audits itself — click to view our public TrustScore"
    >
      <div className="flex items-center gap-1 bg-white border border-[#0A0A0A] pl-2 pr-1 py-1 shadow-[3px_3px_0_0_#0A0A0A] transition-transform group-hover:-translate-y-0.5">
        <a
          href={`/report/${SELF_DOMAIN}`}
          className="flex items-center gap-2 no-underline"
          data-testid="self-badge-link"
        >
          <span className="font-mono text-[9px] uppercase tracking-widest text-[#475569] hidden sm:inline">
            Scanned by
          </span>
          <img
            src={svg}
            alt={`PrivacyLens TrustScore for ${SELF_DOMAIN}`}
            className="block"
            onError={() => setImgFailed(true)}
            width={180}
            height={28}
            loading="lazy"
          />
        </a>
        <button
          type="button"
          onClick={dismiss}
          className="ml-0.5 p-1 text-[#94A3B8] hover:text-[#0A0A0A] hover:bg-[#F1F5F9]"
          aria-label="Dismiss badge"
          title="Dismiss"
          data-testid="self-badge-dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </aside>
  );
}
